/**
 * Meta Account Discovery Service (Story 9.1, Task 2)
 *
 * Discovers Facebook Pages, Ad Accounts, and Instagram Business accounts
 * after successful Meta OAuth flow.
 *
 * Uses safeFetch() for all Meta API calls with AbortController timeout.
 *
 * SECURITY:
 * - /me/accounts response contains page access tokens — parse immediately,
 *   extract tokens, and discard the raw response. Never cache or log it.
 * - Page tokens from a long-lived user token are non-expiring.
 */

import type { AdapterErrorCode } from "../adapter";
import { safeFetch } from "../safe-fetch";
import { META_GRAPH_API_VERSION, type MetaPlatform } from "./scopes";
import { getEnabledMetaPlatforms } from "./oauth";

// ============================================================================
// Constants
// ============================================================================

const META_GRAPH_BASE = `https://graph.facebook.com/${META_GRAPH_API_VERSION}`;
const PER_CALL_TIMEOUT_MS = 5_000;
/** Pause discovery if x-app-usage call_count exceeds this threshold */
const RATE_LIMIT_THRESHOLD = 80;
/**
 * Hard cap on cursor-pagination follows per section (Story 31.20 AC #8).
 * 50 pages × limit=100 = 5,000 resources per section — beyond plausibility
 * for agency use. EXCEEDING the cap marks the section "failed": cap-poisoned
 * catalogs are errors, never silent truncations, and a cursor that re-serves
 * the same `next` URL can no longer loop forever.
 */
export const MAX_DISCOVERY_PAGES = 50;

// ============================================================================
// Types
// ============================================================================

export interface DiscoveredPage {
  id: string;
  name: string;
  category: string;
  /** Page access token — SENSITIVE, encrypt immediately and discard */
  accessToken: string;
  instagramBusinessAccountId: string | null;
}

export interface DiscoveredAdAccount {
  id: string;
  accountId: string;
  name: string;
  currency: string;
}

export interface DiscoveredInstagramAccount {
  id: string;
  linkedPageId: string;
  linkedPageName: string;
}

/**
 * Per-section discovery outcome (Story 31.20 AC #2).
 * - "complete"     — section paginated to completion with no errors
 * - "failed"       — a call failed, the body was malformed, or the page cap was hit
 * - "rate_limited" — x-app-usage threshold cut the section short
 * - "aborted"      — the caller's AbortSignal fired before completion
 * - "skipped"      — section was not requested via `sections` (zero fetches)
 */
export type MetaSectionStatus =
  | "complete"
  | "failed"
  | "rate_limited"
  | "aborted"
  | "skipped";

/**
 * First failing call's safeFetch-shaped error detail (Story 31.20 AC #2),
 * surfaced for adapter-side mapping (e.g. parseMetaGraphError → token_expired).
 */
export interface MetaDiscoveryFirstError {
  /** Typed as AdapterErrorCode so firstError can flow into adapterErr without
   *  an unchecked widening cast (Story 31.20 code review). */
  code: AdapterErrorCode;
  message: string;
  retryable: boolean;
  providerStatus?: number;
  providerMessage?: string;
}

export interface MetaDiscoverySectionStatus {
  pages: MetaSectionStatus;
  adAccounts: MetaSectionStatus;
  instagram: MetaSectionStatus;
}

/** Which Graph API sections to fetch (Story 31.20 AC #1). */
export interface MetaDiscoverySections {
  pages?: boolean;
  adAccounts?: boolean;
  instagram?: boolean;
}

export interface MetaDiscoveryResult {
  pages: DiscoveredPage[];
  adAccounts: DiscoveredAdAccount[];
  instagramAccounts: DiscoveredInstagramAccount[];
  /** Platforms that can be enabled based on discovered accounts */
  enabledPlatforms: MetaPlatform[];
  /** True if discovery was cut short by timeout */
  partial: boolean;
  /** True if x-app-usage > 80% call_count */
  rateLimitWarning: boolean;
  /**
   * Per-section outcome (Story 31.20 AC #2). Strict consumers (the four
   * adapter `discoverAccounts()` implementations) treat anything other than
   * "complete" on a section they depend on as an error; the OAuth callback
   * keeps its best-effort reads and may ignore this field entirely.
   */
  sectionStatus: MetaDiscoverySectionStatus;
  /**
   * The first failing call's error detail across sections (execution order:
   * pages → adAccounts → instagram). Present when any section is "failed"
   * (and sometimes "aborted" when the abort surfaced as a fetch error).
   */
  firstError?: MetaDiscoveryFirstError;
}

export interface MetaDiscoveryOptions {
  accessToken: string;
  /** Granted scopes from token response — determines which platforms to enable */
  grantedScopes: string[];
  /** AbortController signal for timeout budget */
  signal?: AbortSignal;
  /** Base URL override for testing */
  baseUrl?: string;
  /**
   * Which sections to fetch (Story 31.20 AC #1). Defaults to ALL TRUE so the
   * OAuth callback caller compiles and behaves unchanged. A skipped section
   * performs ZERO fetches and reports sectionStatus "skipped".
   * NOTE: the instagram walk uses the pages list as its parent set — callers
   * wanting IG accounts must request `pages: true` as well.
   */
  sections?: MetaDiscoverySections;
}

/** Internal: outcome of one section fetcher. */
interface SectionOutcome {
  status: Exclude<MetaSectionStatus, "skipped">;
  firstError?: MetaDiscoveryFirstError;
}

// ============================================================================
// Rate Limit Header Parsing (Task 2.7)
// ============================================================================

/**
 * Parses Meta's x-app-usage header.
 * Returns call_count percentage or 0 if header is missing/unparseable.
 */
export function parseAppUsage(response: Response): number {
  const header = response.headers.get("x-app-usage");
  if (!header) return 0;
  try {
    const usage = JSON.parse(header) as { call_count?: number };
    return usage.call_count ?? 0;
  } catch {
    return 0;
  }
}

// ============================================================================
// Pages Discovery (Task 2.1, 2.2, 2.3)
// ============================================================================

interface MetaPagesResponse {
  data: Array<{
    id: string;
    name: string;
    access_token: string;
    category: string;
    tasks?: string[];
  }>;
  paging?: {
    cursors: { before: string; after: string };
    next?: string;
  };
}

/**
 * Fetches all Facebook Pages the user manages.
 * Handles cursor-based pagination via `after` parameter.
 *
 * CRITICAL: Response contains page access tokens. Parse immediately and discard.
 */
async function discoverPages(
  accessToken: string,
  baseUrl: string,
  signal?: AbortSignal
): Promise<{ pages: DiscoveredPage[]; rateLimitWarning: boolean } & SectionOutcome> {
  const pages: DiscoveredPage[] = [];
  let rateLimitWarning = false;
  let status: SectionOutcome["status"] = "complete";
  let firstError: MetaDiscoveryFirstError | undefined;
  let fetchedPages = 0;
  // SECURITY: access_token in URL — safeFetch() must NOT log full request URLs.
  // See: Dev Notes — "META_APP_SECRET appears in GET query params"
  let url = `${baseUrl}/me/accounts?fields=id,name,access_token,category,tasks&limit=100&access_token=${accessToken}`;

  while (url) {
    if (signal?.aborted) {
      status = "aborted";
      break;
    }

    const result = await safeFetch(url, { signal }, PER_CALL_TIMEOUT_MS);

    if (!result.success) {
      // Keep partial results (the OAuth callback path is best-effort) but
      // report the failure so strict consumers never see truncation as success.
      status = "failed";
      firstError ??= result.error;
      break;
    }

    const response = result.data;
    const callCount = parseAppUsage(response);

    let data: MetaPagesResponse;
    try {
      data = (await response.json()) as MetaPagesResponse;
    } catch {
      status = "failed";
      firstError ??= {
        code: "invalid_response",
        message: "Malformed JSON body in Meta /me/accounts response",
        retryable: false,
      };
      break;
    }

    if (!Array.isArray(data.data)) {
      status = "failed";
      firstError ??= {
        code: "invalid_response",
        message: "Meta /me/accounts response is missing the data array",
        retryable: false,
      };
      break;
    }

    for (const page of data.data) {
      pages.push({
        id: page.id,
        name: page.name,
        category: page.category,
        accessToken: page.access_token,
        instagramBusinessAccountId: null, // Set during IG discovery
      });
    }

    // Cursor-based pagination — bounded by MAX_DISCOVERY_PAGES (AC #8)
    const next = data.paging?.next ?? "";
    fetchedPages++;

    // Check rate limit AFTER processing current response data. The WARNING
    // always propagates (it gates later sections and the callback budget),
    // but the SECTION is only "rate_limited" when the cut left pages
    // unfetched — a >80% header on the FINAL page is still a complete walk
    // (Story 31.20 code review: otherwise every discovery at high app usage
    // returns adapterErr for a provably complete catalog under the 31-24 cron).
    if (callCount > RATE_LIMIT_THRESHOLD) {
      rateLimitWarning = true;
      if (next) {
        status = "rate_limited";
        break;
      }
    }

    if (next && fetchedPages >= MAX_DISCOVERY_PAGES) {
      status = "failed";
      firstError ??= {
        code: "invalid_response",
        message: `Meta /me/accounts pagination exceeded MAX_DISCOVERY_PAGES (${MAX_DISCOVERY_PAGES}) — refusing cap-truncated catalog`,
        retryable: false,
      };
      break;
    }
    url = next;
  }

  return { pages, rateLimitWarning, status, firstError };
}

// ============================================================================
// Ad Account Discovery (Task 2.5, 2.3)
// ============================================================================

interface MetaAdAccountsResponse {
  data: Array<{
    id: string;
    account_id: string;
    name: string;
    account_status: number;
    currency: string;
  }>;
  paging?: {
    cursors: { before: string; after: string };
    next?: string;
  };
}

/** Meta Ad Account status codes */
const ACTIVE_AD_ACCOUNT_STATUS = 1;

/**
 * Fetches all active Ad Accounts the user has access to.
 * Filters to account_status === 1 (ACTIVE) only.
 * Meta statuses: 1=ACTIVE, 2=DISABLED, 3=UNSETTLED, 7=PENDING_RISK_REVIEW, 100=PENDING_CLOSURE, 101=CLOSED
 */
async function discoverAdAccounts(
  accessToken: string,
  baseUrl: string,
  signal?: AbortSignal
): Promise<{ adAccounts: DiscoveredAdAccount[]; skippedInactive: number; rateLimitWarning: boolean } & SectionOutcome> {
  const adAccounts: DiscoveredAdAccount[] = [];
  let skippedInactive = 0;
  let rateLimitWarning = false;
  let status: SectionOutcome["status"] = "complete";
  let firstError: MetaDiscoveryFirstError | undefined;
  let fetchedPages = 0;
  let url = `${baseUrl}/me/adaccounts?fields=id,account_id,name,account_status,currency&limit=100&access_token=${accessToken}`;

  while (url) {
    if (signal?.aborted) {
      status = "aborted";
      break;
    }

    const result = await safeFetch(url, { signal }, PER_CALL_TIMEOUT_MS);

    if (!result.success) {
      // Keep partial results (callback best-effort) but report the failure.
      status = "failed";
      firstError ??= result.error;
      break;
    }

    const response = result.data;
    const callCount = parseAppUsage(response);

    let data: MetaAdAccountsResponse;
    try {
      data = (await response.json()) as MetaAdAccountsResponse;
    } catch {
      status = "failed";
      firstError ??= {
        code: "invalid_response",
        message: "Malformed JSON body in Meta /me/adaccounts response",
        retryable: false,
      };
      break;
    }

    if (!Array.isArray(data.data)) {
      status = "failed";
      firstError ??= {
        code: "invalid_response",
        message: "Meta /me/adaccounts response is missing the data array",
        retryable: false,
      };
      break;
    }

    for (const account of data.data) {
      if (account.account_status === ACTIVE_AD_ACCOUNT_STATUS) {
        adAccounts.push({
          id: account.id,
          accountId: account.account_id,
          name: account.name,
          currency: account.currency,
        });
      } else {
        skippedInactive++;
      }
    }

    // Cursor-based pagination — bounded by MAX_DISCOVERY_PAGES (AC #8)
    const next = data.paging?.next ?? "";
    fetchedPages++;

    // Check rate limit AFTER processing current response data. Warning always
    // propagates; the SECTION is only "rate_limited" when pages remain
    // unfetched — a >80% header on the FINAL page is still a complete walk
    // (Story 31.20 code review — see discoverPages for the full rationale).
    if (callCount > RATE_LIMIT_THRESHOLD) {
      rateLimitWarning = true;
      if (next) {
        status = "rate_limited";
        break;
      }
    }

    if (next && fetchedPages >= MAX_DISCOVERY_PAGES) {
      status = "failed";
      firstError ??= {
        code: "invalid_response",
        message: `Meta /me/adaccounts pagination exceeded MAX_DISCOVERY_PAGES (${MAX_DISCOVERY_PAGES}) — refusing cap-truncated catalog`,
        retryable: false,
      };
      break;
    }
    url = next;
  }

  return { adAccounts, skippedInactive, rateLimitWarning, status, firstError };
}

// ============================================================================
// Instagram Business Account Discovery (Task 2.4)
// ============================================================================

interface MetaIGBusinessResponse {
  instagram_business_account?: { id: string };
  id: string;
}

/**
 * Discovers Instagram Business accounts by querying each Page's
 * instagram_business_account field. 0 or 1 linked IG account per Page.
 */
async function discoverInstagramAccounts(
  pages: DiscoveredPage[],
  accessToken: string,
  baseUrl: string,
  signal?: AbortSignal
): Promise<{ instagramAccounts: DiscoveredInstagramAccount[]; rateLimitWarning: boolean } & SectionOutcome> {
  const instagramAccounts: DiscoveredInstagramAccount[] = [];
  let rateLimitWarning = false;
  let status: SectionOutcome["status"] = "complete";
  let firstError: MetaDiscoveryFirstError | undefined;

  // First abnormality wins — a later rate-limit break must not mask an
  // earlier per-page failure (firstError pairs with the recorded status).
  const markStatus = (next: SectionOutcome["status"]) => {
    if (status === "complete") status = next;
  };

  for (const [index, page] of pages.entries()) {
    if (signal?.aborted) {
      markStatus("aborted");
      break;
    }

    const url = `${baseUrl}/${page.id}?fields=instagram_business_account&access_token=${accessToken}`;
    const result = await safeFetch(url, { signal }, PER_CALL_TIMEOUT_MS);

    if (!result.success) {
      // Keep collecting other pages (callback best-effort), but mark the
      // section failed so strict consumers see the gap (Story 31.20 AC #2).
      markStatus("failed");
      firstError ??= result.error;
      continue; // Skip this page, try others
    }

    const response = result.data;
    const callCount = parseAppUsage(response);
    if (callCount > RATE_LIMIT_THRESHOLD) {
      rateLimitWarning = true;
      // Only an actual cut marks the section rate_limited: on the LAST parent
      // page no further calls are needed, so parse the already-paid-for body
      // and finish the walk complete (Story 31.20 code review — mirrors the
      // cursor loops' final-page semantics).
      if (index < pages.length - 1) {
        markStatus("rate_limited");
        break;
      }
    }

    let data: MetaIGBusinessResponse;
    try {
      data = (await response.json()) as MetaIGBusinessResponse;
    } catch {
      markStatus("failed");
      firstError ??= {
        code: "invalid_response",
        message: "Malformed JSON body in Meta instagram_business_account response",
        retryable: false,
      };
      continue;
    }

    if (data.instagram_business_account) {
      instagramAccounts.push({
        id: data.instagram_business_account.id,
        linkedPageId: page.id,
        linkedPageName: page.name,
      });

      // Also update the page's instagramBusinessAccountId
      page.instagramBusinessAccountId = data.instagram_business_account.id;
    }
  }

  return { instagramAccounts, rateLimitWarning, status, firstError };
}

// ============================================================================
// Main Discovery Function (Task 2.1)
// ============================================================================

/**
 * Discovers Meta accounts after OAuth flow completes.
 *
 * Fetches Pages, Ad Accounts, and Instagram Business accounts
 * with a 10s total budget via AbortController.
 *
 * Execution order:
 * 1. Fetch Pages (/me/accounts) — includes page access tokens
 * 2. Fetch Ad Accounts (/me/adaccounts) — filter to ACTIVE only
 * 3. For each Page, check for linked Instagram Business account
 *
 * If budget expires, returns partial results with partial=true.
 *
 * Story 31.20: `options.sections` selects which sections to fetch (default
 * all — callback path unchanged), and the result carries per-section
 * `sectionStatus` + `firstError` so strict consumers (the four adapters'
 * `discoverAccounts()`) can refuse truncated catalogs. This function itself
 * NEVER throws on malformed bodies and never refuses partials — strictness
 * is the adapters' interpretation, keeping the OAuth callback best-effort.
 */
export async function discoverMetaAccounts(
  options: MetaDiscoveryOptions
): Promise<MetaDiscoveryResult> {
  const { accessToken, grantedScopes, signal, baseUrl = META_GRAPH_BASE } = options;

  // Story 31.20 AC #1: sections default to ALL TRUE so the OAuth callback
  // caller (apps/server meta-oauth.ts) compiles and behaves unchanged.
  const sections = {
    pages: options.sections?.pages ?? true,
    adAccounts: options.sections?.adAccounts ?? true,
    instagram: options.sections?.instagram ?? true,
  };

  let rateLimitWarning = false;
  let partial = false;
  let firstError: MetaDiscoveryFirstError | undefined;
  const sectionStatus: MetaDiscoverySectionStatus = {
    pages: "skipped",
    adAccounts: "skipped",
    instagram: "skipped",
  };

  // Step 1: Discover Pages
  let pages: DiscoveredPage[] = [];
  if (sections.pages) {
    const pagesResult = await discoverPages(accessToken, baseUrl, signal);
    pages = pagesResult.pages;
    sectionStatus.pages = pagesResult.status;
    firstError ??= pagesResult.firstError;
    if (pagesResult.rateLimitWarning) rateLimitWarning = true;
  }

  // Step 2: Discover Ad Accounts (independent of pages)
  let adAccounts: DiscoveredAdAccount[] = [];
  if (sections.adAccounts) {
    if (!signal?.aborted && !rateLimitWarning) {
      const adResult = await discoverAdAccounts(accessToken, baseUrl, signal);
      adAccounts = adResult.adAccounts;
      sectionStatus.adAccounts = adResult.status;
      firstError ??= adResult.firstError;
      if (adResult.rateLimitWarning) rateLimitWarning = true;
    } else {
      // Never ran — attribute the cut to its cause (AC #2)
      sectionStatus.adAccounts = signal?.aborted ? "aborted" : "rate_limited";
      partial = true;
    }
  }

  // Step 3: Discover Instagram Business accounts (requires pages)
  let instagramAccounts: DiscoveredInstagramAccount[] = [];
  if (sections.instagram) {
    if (pages.length === 0 && sectionStatus.pages === "complete") {
      // Zero parent pages from a COMPLETE pages walk: the IG section needs
      // zero calls, so it is complete even under a rate-limit warning
      // (Story 31.20 code review — a budget warning cannot truncate a walk
      // that has nothing to fetch).
      sectionStatus.instagram = "complete";
    } else if (signal?.aborted || rateLimitWarning) {
      sectionStatus.instagram = signal?.aborted ? "aborted" : "rate_limited";
    } else if (pages.length > 0) {
      const igResult = await discoverInstagramAccounts(pages, accessToken, baseUrl, signal);
      instagramAccounts = igResult.instagramAccounts;
      sectionStatus.instagram = igResult.status;
      firstError ??= igResult.firstError;
      if (igResult.rateLimitWarning) rateLimitWarning = true;
    } else {
      // Zero parent pages: a COMPLETE pages walk with no pages legitimately
      // yields zero IG accounts; a non-complete (or skipped) pages walk means
      // the parent set is unknown, so the IG section cannot claim completeness.
      sectionStatus.instagram = sectionStatus.pages === "complete" ? "complete" : "failed";
    }
  }

  if (signal?.aborted) {
    partial = true;
  }

  // Determine which platforms can be enabled based on scopes AND discovered accounts
  const scopeEnabledPlatforms = getEnabledMetaPlatforms(grantedScopes);
  const enabledPlatforms: MetaPlatform[] = [];

  for (const platform of scopeEnabledPlatforms) {
    switch (platform) {
      case "facebook-pages":
        // Only enable if pages were found
        if (pages.length > 0) enabledPlatforms.push(platform);
        break;
      case "facebook-ads":
        // Enable if ad accounts were found (or discovery was partial — will sync later)
        if (adAccounts.length > 0 || partial) enabledPlatforms.push(platform);
        break;
      case "instagram-business":
        // Enable if IG accounts found, OR if pages exist (may find IG later)
        if (instagramAccounts.length > 0 || pages.length > 0) enabledPlatforms.push(platform);
        break;
      case "instagram-ads":
        // Same as facebook-ads — shares the same ad accounts
        if (adAccounts.length > 0 || partial) enabledPlatforms.push(platform);
        break;
    }
  }

  return {
    pages,
    adAccounts,
    instagramAccounts,
    enabledPlatforms,
    partial,
    rateLimitWarning,
    sectionStatus,
    firstError,
  };
}
